import React, { useState } from "react";
import {
  Buildings, Confetti, Cake, BeerStein, CheckCircle, Sparkle,
  CalendarBlank, Users, WhatsappLogo, ArrowRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, WHATSAPP_NUMBER } from "../lib/api";

const CATA_IMG = "https://static.prod-images.emergentagent.com/jobs/f4d02145-fcf7-41ef-be94-47907979e0b0/images/696b792ae0a315d3f8f63f93ba3dbc4d815095f7c01f26f8246416f1ff7bdee1.jpeg";

const perfectFor = [
  { icon: Buildings, t: "Empresas", d: "Team building y afterworks memorables con cerveza artesanal." },
  { icon: Confetti, t: "Eventos corporativos", d: "Lanzamientos, cierres de año y celebraciones con sello propio." },
  { icon: Cake, t: "Cumpleaños", d: "Una experiencia distinta para festejar entre amigos." },
];

const includes = [
  "Degustación guiada de nuestras cervezas y estilos",
  "Cata comparativa dirigida por nuestro cervecero",
  "Maridaje con snacks y tapas",
  "Torre de tiraje y servicio en tu evento",
  "Historia y proceso artesanal de cada cerveza",
  "Experiencia 100% privada y personalizada",
];

export default function CataPrivada() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", event_type: "empresa", event_date: "", guests: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^\S+@\S+\.\S+$/.test(form.email) || form.phone.trim().length < 7) {
      toast.error("Completa nombre, correo y teléfono válidos");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/cata-requests", {
        ...form,
        guests: form.guests ? parseInt(form.guests) : null,
      });
      setDone(res.data);
      toast.success("¡Solicitud enviada!");
    } catch {
      toast.error("No se pudo enviar. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const whatsapp = () => {
    const msg = `¡Hola Ciudad Garaje! 🍺 Quiero información sobre una CATA PRIVADA.\n\nNombre: ${form.name || "-"}\nTipo de evento: ${form.event_type}\nFecha: ${form.event_date || "por definir"}\nPersonas: ${form.guests || "por definir"}${done ? `\nSolicitud: ${done.reference}` : ""}`;
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative grain overflow-hidden border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 border border-[#D97736]/50 text-[#F5A623] px-3 py-1.5 mb-6">
              <Sparkle size={16} weight="fill" /> <span className="overline text-[#F5A623]">Nueva experiencia</span>
            </div>
            <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl leading-[0.9]">
              Vive una<br /><span className="text-[#D97736]">cata privada</span>
            </h1>
            <p className="text-gray-300 text-base md:text-lg mt-6 max-w-lg leading-relaxed">
              Llevamos todas nuestras cervezas y estilos a tu evento. Una experiencia comparativa, guiada y privada para empresas, fiestas corporativas y cumpleaños.
            </p>
            <div className="flex flex-wrap gap-4 mt-9">
              <a href="#reservar" className="flex items-center gap-2 bg-[#D97736] hover:bg-[#E88D50] text-black font-semibold uppercase tracking-wide px-7 py-4 transition-colors hover:-translate-y-0.5" style={{ transitionProperty: "background-color, transform" }} data-testid="cata-reservar-cta">
                Reservar mi cata <ArrowRight size={18} weight="bold" />
              </a>
              <button onClick={whatsapp} className="flex items-center gap-2 border border-[#25D366]/50 text-[#25D366] hover:bg-[#25D366]/10 uppercase tracking-wide px-7 py-4 transition-colors">
                <WhatsappLogo size={20} weight="fill" /> Consultar
              </button>
            </div>
          </div>

          <div className="mx-auto w-full max-w-sm">
            <img src={CATA_IMG} alt="Vive una cata privada" className="w-full border border-white/10" />
          </div>
        </div>
      </section>

      {/* PERFECTO PARA */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20">
        <div className="overline text-[#F5A623] mb-3">Perfecto para</div>
        <h2 className="font-display text-5xl md:text-6xl leading-none mb-10">Cada ocasión especial</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {perfectFor.map((p) => (
            <div key={p.t} className="bg-[#141414] p-8 hover:bg-[#1a1a1a] transition-colors" data-testid={`cata-for-${p.t}`}>
              <div className="w-12 h-12 border border-[#D97736]/50 flex items-center justify-center text-[#D97736] mb-5"><p.icon size={24} weight="duotone" /></div>
              <h3 className="font-display text-3xl">{p.t}</h3>
              <p className="text-gray-400 text-sm mt-2">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* INCLUYE */}
      <section className="border-y border-white/10 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="overline text-[#F5A623] mb-3 flex items-center gap-2"><BeerStein size={16} weight="fill" /> La experiencia</div>
            <h2 className="font-display text-5xl md:text-6xl leading-none">¿Qué incluye?</h2>
            <p className="text-gray-400 mt-4 max-w-md">Una cata a la medida de tu evento, con la calidad artesanal de Ciudad Garaje: del producto a tu boca.</p>
          </div>
          <div className="space-y-px bg-white/10 border border-white/10">
            {includes.map((it) => (
              <div key={it} className="bg-[#141414] p-4 flex items-center gap-3">
                <CheckCircle size={22} weight="fill" className="text-[#D97736] shrink-0" />
                <span className="text-gray-200 text-sm">{it}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORM */}
      <section id="reservar" className="max-w-3xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <div className="text-center mb-10">
          <div className="overline text-[#F5A623] mb-2">Reserva</div>
          <h2 className="font-display text-5xl md:text-6xl leading-none">Solicita tu cata</h2>
          <p className="text-gray-400 mt-3">Déjanos tus datos y te contactamos para armar tu evento.</p>
        </div>

        {done ? (
          <div className="border border-[#25D366]/40 bg-[#141414] p-8 text-center" data-testid="cata-success">
            <CheckCircle size={56} weight="fill" className="mx-auto text-[#25D366]" />
            <h3 className="font-display text-4xl mt-5">¡Gracias, {done.name?.split(" ")[0]}!</h3>
            <p className="text-gray-300 mt-3">Recibimos tu solicitud <span className="text-[#D97736] font-semibold">{done.reference}</span>. Te contactaremos muy pronto para coordinar tu cata privada. 🍺</p>
            <button onClick={whatsapp} className="mt-6 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-black font-semibold uppercase tracking-wide px-6 py-4 transition-colors" data-testid="cata-whatsapp-btn">
              <WhatsappLogo size={20} weight="fill" /> Adelantar por WhatsApp
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-[#171717] border border-white/10 p-6 md:p-8 space-y-6" data-testid="cata-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="overline block mb-2">Nombre</label>
                <input value={form.name} onChange={(e) => set("name", e.target.value)} className="w-full bg-transparent border-b border-white/20 focus:border-[#D97736] outline-none py-3 text-white" placeholder="Tu nombre" data-testid="cata-name" />
              </div>
              <div>
                <label className="overline block mb-2">Correo</label>
                <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="w-full bg-transparent border-b border-white/20 focus:border-[#D97736] outline-none py-3 text-white" placeholder="tucorreo@ejemplo.com" data-testid="cata-email" />
              </div>
              <div>
                <label className="overline block mb-2">Teléfono</label>
                <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="w-full bg-transparent border-b border-white/20 focus:border-[#D97736] outline-none py-3 text-white" placeholder="0414 000 0000" data-testid="cata-phone" />
              </div>
              <div>
                <label className="overline block mb-2">Tipo de evento</label>
                <select value={form.event_type} onChange={(e) => set("event_type", e.target.value)} className="w-full bg-[#171717] border-b border-white/20 focus:border-[#D97736] outline-none py-3 text-white" data-testid="cata-event-type">
                  <option value="empresa">Empresa</option>
                  <option value="corporativo">Evento corporativo</option>
                  <option value="cumpleanos">Cumpleaños</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
              <div>
                <label className="overline block mb-2 flex items-center gap-1"><CalendarBlank size={14} /> Fecha tentativa</label>
                <input type="date" value={form.event_date} onChange={(e) => set("event_date", e.target.value)} className="w-full bg-transparent border-b border-white/20 focus:border-[#D97736] outline-none py-3 text-white" data-testid="cata-date" />
              </div>
              <div>
                <label className="overline block mb-2 flex items-center gap-1"><Users size={14} /> N° de personas</label>
                <input type="number" min="1" value={form.guests} onChange={(e) => set("guests", e.target.value)} className="w-full bg-transparent border-b border-white/20 focus:border-[#D97736] outline-none py-3 text-white" placeholder="Ej: 15" data-testid="cata-guests" />
              </div>
            </div>
            <div>
              <label className="overline block mb-2">Cuéntanos de tu evento (opcional)</label>
              <textarea rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} className="w-full bg-transparent border border-white/20 focus:border-[#D97736] outline-none p-3 text-white text-sm" placeholder="Lugar, horario, preferencias..." data-testid="cata-message" />
            </div>
            <button type="submit" disabled={loading} className="w-full bg-[#D97736] hover:bg-[#E88D50] disabled:opacity-60 text-black font-semibold uppercase tracking-wide py-4 transition-colors" data-testid="cata-submit">
              {loading ? "Enviando..." : "Solicitar mi cata privada"}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
